import { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { findRfiColorMeta } from "../../utils/serviceCategoriesColors";

export function VendorExpandableStats({ stats, detailsData }) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const rfisState = useSelector((state) => state.rfisColors);
  const rfisList = rfisState?.rfis || [];

  // const gridColsClass =
  //   stats.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-4";
  const gridColsClass = "lg:grid-cols-" + (stats.length || 3);

  return (
    <div className="bg-white -mx-4 sm:-mx-6 lg:-mx-8">
      {/* Stats Row */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass}`}>
        {stats.map((stat, statIdx) => {
          // Border logic
          let borderClass = "";
          if (statIdx % 2 === 1) {
            borderClass = "sm:border-l";
          }
          if (stats.length === 5) {
            if (statIdx === 2 || statIdx === 3 || statIdx === 4) {
              borderClass += " lg:border-l";
            }
          } else {
            if (statIdx === 2) {
              borderClass += " lg:border-l";
            }
          }

          return (
            <div
              key={stat.name}
              className={`${borderClass} border-t border-gray-200 py-6 ${statIdx === 0 ? "pl-12 pr-4 sm:pl-14 sm:pr-6 lg:pl-16 lg:pr-8" : "px-4 sm:px-6 lg:px-8"}`}
            >
              <p className="text-sm font-medium text-gray-600">{stat.name}</p>
              <p className="mt-2 flex items-baseline gap-x-2">
                <span className="text-4xl font-semibold tracking-tight text-gray-900">
                  {stat.value}
                </span>
                {stat.separator && (
                  <>
                    <span className="text-4xl font-semibold tracking-tight text-gray-400">
                      {stat.separator}
                    </span>
                    <span
                      className={`text-4xl font-semibold tracking-tight ${stat.subValueColor || "text-gray-900"}`}
                    >
                      {stat.subValue || "–"}
                    </span>
                  </>
                )}
                {stat.unit ? (
                  <span className="text-sm text-gray-500">{stat.unit}</span>
                ) : null}
              </p>
            </div>
          );
        })}
      </div>

      {/* Single Centered Expand Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="py-2 px-4 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
        >
          {isExpanded ? (
            <ChevronUpIcon className="h-5 w-5" />
          ) : (
            <ChevronDownIcon className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Expanded Details Section - All Stats */}
      {isExpanded && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridColsClass} border-t border-gray-200`}
        >
          {stats.map((stat, statIdx) => {
            const details = detailsData && detailsData[stat.name];

            // Border logic
            let borderClass = "";
            if (statIdx % 2 === 1) {
              borderClass = "sm:border-l";
            }
            if (stats.length === 5) {
              if (statIdx === 2 || statIdx === 3 || statIdx === 4) {
                borderClass += " lg:border-l";
              }
            } else {
              if (statIdx === 2) {
                borderClass += " lg:border-l";
              }
            }

            return (
              <div
                key={`details-${stat.name}`}
                className={`${borderClass} border-t border-gray-100 py-4 ${statIdx === 0 ? "pl-12 pr-4 sm:pl-14 sm:pr-6 lg:pl-16 lg:pr-8" : "px-4 sm:px-6 lg:px-8"}`}
              >
                {details && details.length > 0 && (
                  <div className="space-y-2 text-sm">
                    {/* Check if this is the Completed VQQ Templates stat - display as flex row */}
                    {stat.name === "Completed VQQ Templates" ? (
                      <div className="flex flex-wrap gap-2">
                        {details.map((detail, idx) => {
                          const meta = findRfiColorMeta(detail, rfisList) || {};
                          return (
                            <span
                              key={idx}
                              style={{
                                backgroundColor: meta.bg || "#eaeaea",
                                color: meta.text || "#111",
                                boxShadow: meta.border
                                  ? `inset 0 0 0 1px ${meta.border}1A`
                                  : undefined,
                              }}
                              className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium mr-2 mb-1"
                            >
                              {detail.label?.length > 30 ? detail.label.slice(0, 30) + "..." : detail.label}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      // All other stats - display as list
                      details.map((detail, idx) => {
                        const meta = detail.badge ? findRfiColorMeta(detail, rfisList) || {} : null;
                        const badgeStyle = meta
                          ? {
                              backgroundColor: meta.bg || "#eaeaea",
                              color: meta.text || "#111",
                              boxShadow: meta.border
                                ? `inset 0 0 0 1px ${meta.border}1A`
                                : undefined,
                            }
                          : undefined;
                        const badgeClass = `inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${!meta ? (detail.badgeColor || "bg-gray-100 text-gray-700") : ""}`;
                        return (
                        <div
                          key={idx}
                          className="flex items-center justify-between py-1"
                        >
                          <div className="flex items-center gap-2">
                            {detail.badge ? (
                              <span
                                className={badgeClass}
                                style={badgeStyle}
                              >
                                {detail.label}
                              </span>
                            ) : detail.link ? (
                              <span
                                onClick={() => navigate(detail.link)}
                                className="text-indigo-600 hover:text-indigo-500 cursor-pointer"
                              >
                                {detail.label}
                              </span>
                            ) : (
                              <span className="text-gray-600">
                                {detail.label}
                              </span>
                            )}
                          </div>
                          {detail.value && (
                            <span className="text-gray-900 font-medium">
                              {detail.value}
                            </span>
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
